/**
 * Section model + pagination for the measurements report pages.
 *
 * ReportPage is a fixed-height A4 sheet that clips overflow, so the
 * measurements content (open-ended: custom diameters, user-added C-arm
 * views, …) must be split across as many pages as it needs. Splits only
 * happen at section boundaries — a section whose rows would cross the
 * page limit starts the next page.
 */

/** Minimal measurement shape the section builder reads (structural subset of MeasurementResult). */
export interface MeasurementRowSource {
    readonly id: string;
    readonly name: string;
    readonly value: number;
    readonly unit: string;
}

/** Structural subset of SizingStateSnapshot consumed by buildMeasurementSections. */
export interface MeasurementSectionsInput {
    readonly measurements: {
        readonly lengths: readonly MeasurementRowSource[];
        readonly angles: readonly MeasurementRowSource[];
        readonly derived: readonly MeasurementRowSource[];
    };
    readonly diameterGroups: readonly {
        readonly main: MeasurementRowSource;
        readonly offsets: readonly MeasurementRowSource[];
    }[];
    readonly minMaxDiameters: readonly MeasurementRowSource[];
    readonly customDiameters: readonly MeasurementRowSource[];
}

/** Structural subset of CarmView consumed by buildMeasurementSections. */
export interface CarmRowSource {
    readonly id: string;
    readonly name: string;
    readonly craniocaudal: number;
    readonly oblique: number;
}

export type MeasurementRow =
    | { kind: 'value'; id: string; name: string; value: number; unit: string; indented?: boolean }
    | { kind: 'carm'; id: string; name: string; craniocaudal: number; oblique: number };

export interface MeasurementSection {
    key: string;
    title: string;
    rows: MeasurementRow[];
}

/**
 * Max rendered rows (section titles + value rows) per measurements page.
 * Beyond this the content splits onto the next page at a section boundary.
 */
export const MAX_MEASUREMENT_ROWS_PER_PAGE = 36;

function valueRow(m: MeasurementRowSource, indented?: boolean): MeasurementRow {
    return { kind: 'value', id: m.id, name: m.name, value: m.value, unit: m.unit, indented };
}

/**
 * Build the non-empty measurement sections in display order:
 * Diameters, Lengths, Angles, Shape, C-arm Views.
 */
export function buildMeasurementSections(
    sizing: MeasurementSectionsInput | null,
    carmViews: readonly CarmRowSource[] | null
): MeasurementSection[] {
    const sections: MeasurementSection[] = [];

    if (sizing) {
        const diameterRows: MeasurementRow[] = [
            ...sizing.diameterGroups.flatMap((group) => [
                valueRow(group.main),
                ...group.offsets.map((offset) => valueRow(offset, true)),
            ]),
            ...sizing.minMaxDiameters.map((m) => valueRow(m)),
            ...sizing.customDiameters.map((m) => valueRow(m)),
        ];
        const categories: [string, MeasurementRow[]][] = [
            ['Diameters', diameterRows],
            ['Lengths', sizing.measurements.lengths.map((m) => valueRow(m))],
            ['Angles', sizing.measurements.angles.map((m) => valueRow(m))],
            ['Shape', sizing.measurements.derived.map((m) => valueRow(m))],
        ];
        for (const [title, rows] of categories) {
            if (rows.length > 0) sections.push({ key: title.toLowerCase(), title, rows });
        }
    }

    if (carmViews && carmViews.length > 0) {
        sections.push({
            key: 'carm-views',
            title: 'C-arm Views',
            rows: carmViews.map((view) => ({
                kind: 'carm',
                id: view.id,
                name: view.name,
                craniocaudal: view.craniocaudal,
                oblique: view.oblique,
            })),
        });
    }

    return sections;
}

/**
 * Greedily pack sections onto pages of at most maxRowsPerPage rows, where a
 * section costs its row count plus one for its title. Sections are atomic: one
 * that would cross the limit starts the next page, and a single section larger
 * than a whole page still gets its own page (and may clip).
 */
export function paginateSections(
    sections: readonly MeasurementSection[],
    maxRowsPerPage: number
): MeasurementSection[][] {
    const pages: MeasurementSection[][] = [];
    let current: MeasurementSection[] = [];
    let currentRows = 0;

    for (const section of sections) {
        const cost = section.rows.length + 1;
        if (current.length > 0 && currentRows + cost > maxRowsPerPage) {
            pages.push(current);
            current = [];
            currentRows = 0;
        }
        current.push(section);
        currentRows += cost;
    }
    if (current.length > 0) pages.push(current);

    return pages;
}
